// src/pages/portal/PortalExpressInterest.js — lets a partner flag interest
// in a LIVE mandate they aren't yet engaged on (migrations/043_mandate_interest.sql,
// src/controllers/portalController.js: expressMandateInterest). Reached from
// the "Interested in engaging on this mandate?" link on PortalMandateDetail,
// shown only when !mandate.engaged.
//
// Contact info (name, email) is read-only and pulled from the signed-in
// account, not typed by the partner — same pattern as PortalSettings'
// Account section, and the same reasoning as Add Names' submitter line:
// identity should come from who's authenticated, not a form field. The
// notes field is the only thing the partner actually fills in.
import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '../../lib/api'
import MandateHeader from '../../components/portal/MandateHeader'
import { mandateOverviewTitle, PORTAL_MANDATES_TITLE } from '../../lib/portalPageTitles'
import { useAuth } from '../../context/AuthContext'

export default function PortalExpressInterest() {
  const { id } = useParams()
  const { user, profile } = useAuth()
  const [notes, setNotes] = useState('')
  const [err, setErr] = useState(null)
  const [justSubmitted, setJustSubmitted] = useState(false)

  const { data: mandate, isLoading, error: loadError } = useQuery({
    queryKey: ['portalMandateDetail', id],
    queryFn: () => api.get(`/api/portal/mandates/${id}`).then(r => r.data.data),
  })

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/api/portal/mandates/${id}/interest`, { notes: notes.trim() || undefined }),
    onSuccess: () => {
      setErr(null)
      setJustSubmitted(true)
    },
    onError: (e) => setErr(e.response?.data?.error || e.message),
  })

  function handleSubmit(e) {
    e.preventDefault()
    submitMutation.mutate()
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (loadError || !mandate) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Mandate not found</h3>
          <p><Link to="/portal/mandates">Back to {PORTAL_MANDATES_TITLE}</Link></p>
        </div>
      </div>
    )
  }

  if (mandate.engaged) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Not available</h3>
          <p>You're already engaged on this mandate.</p>
          <p><Link to={`/portal/mandates/${id}`}>Back to {mandateOverviewTitle(mandate)}</Link></p>
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <MandateHeader
        mandate={mandate}
        back={{ to: `/portal/mandates/${id}`, label: mandateOverviewTitle(mandate) }}
        showSector={false}
        title="Interested in Engaging?"
        description="Let RGA know you'd like to engage on this mandate."
      />

      <div className="card" style={{ padding: 20, maxWidth: 480 }}>
        {justSubmitted ? (
          <div style={{
            background: 'var(--success-muted)', border: '1px solid rgba(52,211,153,0.25)',
            borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#34d399',
          }}>
            Thanks — RGA will follow up.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {err && (
              <div style={{
                background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171', marginBottom: 16,
              }}>{err}</div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="input-group">
                <label className="input-label">Name</label>
                <input className="input" value={profile?.name || '—'} readOnly style={{ opacity: 0.7, cursor: 'default' }} />
              </div>
              <div className="input-group">
                <label className="input-label">Email</label>
                <input className="input" value={user?.email || '—'} readOnly style={{ opacity: 0.7, cursor: 'default' }} />
              </div>
            </div>

            <div className="input-group" style={{ marginBottom: 16 }}>
              <label className="input-label">Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <textarea className="textarea" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Anything RGA should know — your interest, relevant relationships, timing, etc." />
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitMutation.isPending}>
              {submitMutation.isPending ? 'Submitting…' : 'Submit'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
